import { TimesheetStatusEnum } from '@nxsys/constants/AppEnums';
import { TimeSheetDataItem } from '@nxsys/types/models/timesheet';
import { isEmpty } from 'lodash';

/**
 * NX-5237: pure decision behind `handleOpenChangeTaxPeriod`.
 *
 * A tax period can only be changed for pre-payroll timesheets, and a single
 * numeric period means different things per pay frequency (e.g. 4 = tax week 4
 * for WEEKLY but tax month 4 for MONTHLY), so a mixed-frequency selection is
 * blocked. Extracted from the component so the guard is unit-testable.
 */
export type ChangeTaxPeriodSelection =
  | { status: 'empty' }
  | { status: 'noneEligible' }
  | { status: 'mixedFrequency' }
  | { status: 'ok'; selected: TimeSheetDataItem[] };

const isEligible = (item: TimeSheetDataItem): boolean =>
  item?.approveStatus !== TimesheetStatusEnum.CANCEL &&
  !item?.newDraftPayroll &&
  !item?.newPayroll;

export const resolveChangeTaxPeriodSelection = (
  list: TimeSheetDataItem[],
): ChangeTaxPeriodSelection => {
  if (isEmpty(list)) return { status: 'empty' };

  // AC 6 (Victor PR #1531): ineligible rows (already in payroll/draft, or
  // cancelled) are NOT dropped here — they are passed through so the BE reports
  // each one per-row in `failed` (single source of truth), instead of silently
  // vanishing from the selection. Only block when NOTHING could ever succeed.
  if (!list.some(isEligible)) return { status: 'noneEligible' };

  // A single numeric period means different things per pay frequency, so a
  // mixed-frequency selection is blocked. Checked across the WHOLE selection to
  // match the BE (assertUniformPayFrequency considers every id, eligible or not)
  // — otherwise the BE would reject the whole batch with a raw 400.
  const frequencies = new Set(
    list.map(
      (item) => item?.subcontractorRef?.payFrequency ?? item?.payFrequency,
    ),
  );
  if (frequencies.size > 1) return { status: 'mixedFrequency' };

  return { status: 'ok', selected: list };
};
